'use client';

import { useState, useEffect, Suspense } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { supabase } from '@/lib/supabase';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';
import Link from 'next/link';

type ApprovalStatus = 'pending' | 'rejected' | 'approved' | null;

// ── Auth error normalizer ─────────────────────────────────────────────────────
function normalizeError(msg: string): string {
  const lower = msg.toLowerCase();
  if (lower.includes('invalid login credentials') || lower.includes('invalid credentials')) {
    return 'Invalid email or password. Please check your credentials and try again.';
  }
  if (lower.includes('email not confirmed')) {
    return 'Please confirm your email address before signing in. Check your inbox for a confirmation link.';
  }
  if (lower.includes('too many requests')) {
    return 'Too many login attempts. Please wait a few minutes and try again.';
  }
  if (lower.includes('network') || lower.includes('fetch')) {
    return 'Network error. Please check your connection and try again.';
  }
  return msg || 'An unexpected error occurred. Please try again.';
}

export default function VendorLoginPage() {
  return (
    <Suspense fallback={<div className="min-h-screen bg-background flex items-center justify-center"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>}>
      <VendorLoginContent />
    </Suspense>
  );
}

function VendorLoginContent() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [mode, setMode] = useState<'signin' | 'register'>('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [storeName, setStoreName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [approvalStatus, setApprovalStatus] = useState<ApprovalStatus>(null);
  const [vendorName, setVendorName] = useState('');
  const [sessionExpiredBanner, setSessionExpiredBanner] = useState(false);

  // Show session expired banner if redirected from RoleGuard
  useEffect(() => {
    if (searchParams?.get('reason') === 'session_expired') {
      setSessionExpiredBanner(true);
    }
  }, [searchParams]);

  // ── Approval status screens ──────────────────────────────────────────────
  if (approvalStatus === 'pending') {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center p-4">
        <div className="w-full max-w-md text-center">
          <div className="w-20 h-20 bg-amber-100 dark:bg-amber-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
            <Icon name="ClockIcon" size={40} className="text-amber-600" />
          </div>
          <h1 className="text-2xl font-bold text-foreground mb-2">Application Under Review</h1>
          <p className="text-muted-foreground mb-2">
            Hi <span className="font-medium text-foreground">{vendorName || 'there'}</span>,
          </p>
          <p className="text-muted-foreground mb-6">
            Your store is currently under review by our compliance team. We verify all liquor licenses and business documents before granting access. This typically takes 1–3 business days.
          </p>
          <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-xl p-4 mb-6 text-left">
            <div className="flex items-start gap-3">
              <Icon name="InformationCircleIcon" size={18} className="text-amber-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-amber-800 dark:text-amber-200">What happens next?</p>
                <ul className="text-xs text-amber-700 dark:text-amber-300 mt-1 space-y-1 list-disc list-inside">
                  <li>Our team reviews your liquor license and business documents</li>
                  <li>You will receive an email once your application is approved</li>
                  <li>After approval, you can access your full vendor dashboard</li>
                </ul>
              </div>
            </div>
          </div>
          <button
            onClick={async () => {
              await supabase.auth.signOut();
              setApprovalStatus(null);
              setEmail('');
              setPassword('');
            }}
            className="w-full py-2.5 bg-muted text-foreground rounded-lg text-sm font-medium hover:bg-muted/80 transition-colors"
          >
            Sign Out
          </button>
        </div>
      </div>
    );
  }

  if (approvalStatus === 'rejected') {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center p-4">
        <div className="w-full max-w-md text-center">
          <div className="w-20 h-20 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
            <Icon name="XCircleIcon" size={40} className="text-red-600" />
          </div>
          <h1 className="text-2xl font-bold text-foreground mb-2">Application Not Approved</h1>
          <p className="text-muted-foreground mb-6">
            Unfortunately, your vendor application was not approved at this time. This may be due to missing documentation, license issues, or compliance requirements.
          </p>
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-xl p-4 mb-6 text-left">
            <div className="flex items-start gap-3">
              <Icon name="ExclamationTriangleIcon" size={18} className="text-red-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-red-800 dark:text-red-200">Next steps</p>
                <ul className="text-xs text-red-700 dark:text-red-300 mt-1 space-y-1 list-disc list-inside">
                  <li>Contact our vendor support team for details</li>
                  <li>Email: vendors@zalchos.com</li>
                  <li>Ensure your liquor license is valid and up to date</li>
                  <li>You may reapply after resolving any outstanding issues</li>
                </ul>
              </div>
            </div>
          </div>
          <button
            onClick={async () => {
              await supabase.auth.signOut();
              setApprovalStatus(null);
              setEmail('');
              setPassword('');
            }}
            className="w-full py-2.5 bg-muted text-foreground rounded-lg text-sm font-medium hover:bg-muted/80 transition-colors"
          >
            Sign Out
          </button>
        </div>
      </div>
    );
  }

  // ── Sign In handler ──────────────────────────────────────────────────────
  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Step 1: Authenticate
    const { data, error: signInError } = await supabase.auth.signInWithPassword({
      email: email.trim().toLowerCase(),
      password,
    });

    if (signInError) {
      console.error('[VendorLogin] Sign-in error:', signInError);
      setError(normalizeError(signInError.message));
      setLoading(false);
      return;
    }

    const userId = data?.user?.id;
    if (!userId) {
      setError('Authentication failed. Please try again.');
      setLoading(false);
      return;
    }

    console.info(`[VendorLogin] Authenticated userId=${userId}`);

    // Step 2: Resolve role — try metadata first (no DB round-trip)
    let role = data?.user?.user_metadata?.role || data?.user?.app_metadata?.role || '';

    // If role not in metadata, try RPC sync
    if (!role) {
      try {
        const { data: syncedRole } = await supabase.rpc('ensure_admin_role_on_signin', {
          signin_user_id: userId,
          signin_email: email.trim().toLowerCase(),
        });
        role = syncedRole || '';
        console.info(`[VendorLogin] Role from RPC: ${role}`);
      } catch (rpcErr) {
        console.warn('[VendorLogin] Role sync RPC error (non-fatal):', rpcErr);
      }
    }

    // Fallback: profiles table
    if (!role) {
      const { data: profile } = await supabase
        .from('profiles')
        .select('role')
        .eq('id', userId)
        .single();
      role = profile?.role || '';
      console.info(`[VendorLogin] Role from profiles: ${role}`);
    }

    console.info(`[VendorLogin] Resolved role: ${role}`);

    // Step 3: Block non-vendor roles
    if (role === 'admin') {
      await supabase.auth.signOut();
      setError('This is the Vendor portal. Please use the Admin portal to sign in.');
      setLoading(false);
      return;
    }

    if (role && role !== 'vendor') {
      await supabase.auth.signOut();
      setError('Vendor access only. Please use the mobile app for your account type.');
      setLoading(false);
      return;
    }

    // Step 4: Check vendor approval status — BLOCK rejected vendors
    let vendorApprovalStatus: string | null = null;
    let vendorStoreName = '';

    // Check vendor_profiles first (primary table)
    const { data: vpRow } = await supabase
      .from('vendor_profiles')
      .select('approval_status, status, business_name, owner_name, store_name, onboarding_status')
      .eq('user_id', userId)
      .maybeSingle();

    if (vpRow) {
      // Check approval_status first, then status, then onboarding_status
      const rawStatus = vpRow.approval_status || vpRow.status || vpRow.onboarding_status || '';
      if (rawStatus === 'approved' || rawStatus === 'active' || rawStatus === 'stripe_connect_complete') {
        vendorApprovalStatus = 'approved';
      } else if (rawStatus === 'rejected' || rawStatus === 'suspended') {
        vendorApprovalStatus = 'rejected';
      } else {
        vendorApprovalStatus = 'pending';
      }
      vendorStoreName = vpRow.business_name || vpRow.store_name || vpRow.owner_name || '';
    }

    // Fallback: check vendors table
    if (!vendorApprovalStatus) {
      const { data: vendorRow } = await supabase
        .from('vendors')
        .select('approval_status, store_name, name, status')
        .eq('user_id', userId)
        .maybeSingle();

      if (vendorRow) {
        const rawStatus = vendorRow.approval_status || vendorRow.status || '';
        if (rawStatus === 'approved' || rawStatus === 'active') {
          vendorApprovalStatus = 'approved';
        } else if (rawStatus === 'rejected' || rawStatus === 'suspended') {
          vendorApprovalStatus = 'rejected';
        } else {
          vendorApprovalStatus = 'pending';
        }
        vendorStoreName = vendorRow.store_name || vendorRow.name || '';
      }
    }

    // No vendor record found → treat as pending (new registration)
    if (!vendorApprovalStatus) {
      vendorApprovalStatus = 'pending';
    }

    console.info(`[VendorLogin] Vendor approval status: ${vendorApprovalStatus}`);
    setVendorName(vendorStoreName);

    // Step 5: Gate access based on approval status
    if (vendorApprovalStatus === 'rejected') {
      await supabase.auth.signOut();
      setApprovalStatus('rejected');
      setLoading(false);
      return;
    }

    if (vendorApprovalStatus === 'pending') {
      await supabase.auth.signOut();
      setApprovalStatus('pending');
      setLoading(false);
      return;
    }

    // Step 6: Approved → small delay to ensure session cookie is set, then redirect
    console.info('[VendorLogin] Approved vendor — redirecting to dashboard');
    // Use replace to avoid back-button loop to login
    router.replace('/apps/vendor');
  };

  // ── Register handler ─────────────────────────────────────────────────────
  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      setLoading(false);
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      setLoading(false);
      return;
    }
    if (!storeName.trim()) {
      setError('Store name is required.');
      setLoading(false);
      return;
    }

    const { data, error: signUpError } = await supabase.auth.signUp({
      email: email.trim().toLowerCase(),
      password,
      options: {
        data: {
          role: 'vendor',
          full_name: storeName.trim(),
          store_name: storeName.trim(),
        },
      },
    });

    if (signUpError) {
      console.error('[VendorRegister] Sign-up error:', signUpError);
      setError(normalizeError(signUpError.message));
      setLoading(false);
      return;
    }

    const userId = data?.user?.id;
    if (userId) {
      // Assign vendor role
      try {
        await supabase.rpc('register_as_vendor', {
          reg_user_id: userId,
          reg_email: email.trim().toLowerCase(),
          store_name: storeName.trim(),
        });
      } catch (rpcErr) {
        console.warn('[VendorRegister] register_as_vendor RPC failed, using fallback:', rpcErr);
        await supabase.from('user_roles').upsert(
          { user_id: userId, email: email.trim().toLowerCase(), role: 'vendor' },
          { onConflict: 'user_id' },
        );
        await supabase
          .from('profiles')
          .update({ role: 'vendor' })
          .eq('id', userId);
      }

      // Create vendor_profiles record with pending status
      const { error: vpError } = await supabase.from('vendor_profiles').upsert(
        {
          user_id: userId,
          business_name: storeName.trim(),
          store_name: storeName.trim(),
          status: 'pending',
          approval_status: 'pending',
          onboarding_status: 'pending_registration',
        },
        { onConflict: 'user_id' },
      );
      if (vpError) {
        console.warn('[VendorRegister] vendor_profiles upsert error (non-fatal):', vpError);
      }

      // Also create vendors record for compatibility
      await supabase.from('vendors').upsert(
        {
          user_id: userId,
          name: storeName.trim(),
          store_name: storeName.trim(),
          email: email.trim().toLowerCase(),
          status: 'pending',
          approval_status: 'pending',
        },
        { onConflict: 'user_id' },
      ).select().maybeSingle();

      // Fire-and-forget: send confirmation email
      fetch('/api/notifications/send-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to: email.trim().toLowerCase(),
          toName: storeName.trim(),
          subject: "Zalchos — We've Received Your Vendor Application",
          emailType: 'vendor_approval',
          fromAddress: 'vendors@zalchos.com',
          templateData: {
            vendorEmail: email.trim().toLowerCase(),
            vendorName: storeName.trim(),
            storeName: storeName.trim(),
            isApplicationReceived: true,
          },
        }),
      }).catch((err) => console.warn('[VendorRegister] Email send failed (non-fatal):', err));

      // Fire-and-forget: notify admins
      fetch('/api/notifications/vendor-application', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          vendorId: userId,
          vendorEmail: email.trim().toLowerCase(),
          storeName: storeName.trim(),
        }),
      }).catch((err) => console.warn('[VendorRegister] Admin notification failed (non-fatal):', err));
    }

    setSuccess(
      'Vendor account created! Your application is under review. You will receive an email once approved. Please check your email to confirm your account.',
    );
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center p-4 py-12">
      <div className="w-full max-w-md">
        {/* Brand Header */}
        <div className="text-center mb-10">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="w-14 h-14 bg-white rounded-xl flex items-center justify-center shadow-lg overflow-hidden border border-border">
              <AppImage
                src="/assets/images/IMG_0046-1768448408663.jpeg"
                alt="Zalchos Logo"
                width={56}
                height={56}
                className="object-contain"
              />
            </div>
          </div>
          <h1 className="text-3xl font-bold text-primary mb-1">Zalchos Partner</h1>
          <p className="text-muted-foreground text-sm">Vendor Portal — Manage your store</p>
        </div>

        <div className="bg-card rounded-2xl shadow-lg border border-border p-8">
          {/* Mode Toggle */}
          <div className="flex rounded-lg bg-muted p-1 mb-7">
            {(['signin', 'register'] as const).map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => { setMode(m); setError(''); setSuccess(''); }}
                className={`flex-1 py-2 text-sm font-medium rounded-md transition-colors ${
                  mode === m
                    ? 'bg-card text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {m === 'signin' ? 'Sign In' : 'Apply as Vendor'}
              </button>
            ))}
          </div>

          {/* Icon + Title */}
          <div className="text-center mb-7">
            <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mx-auto mb-3">
              <Icon
                name={mode === 'signin' ? 'BuildingStorefrontIcon' : 'UserPlusIcon'}
                size={24}
                className="text-primary"
              />
            </div>
            <h2 className="text-xl font-bold text-foreground">
              {mode === 'signin' ? 'Vendor Sign In' : 'Apply as a Vendor'}
            </h2>
            <p className="text-xs text-muted-foreground mt-1">
              {mode === 'signin' ? 'Access your Zalchos Partner dashboard' : 'Register your store on the Zalchos platform'}
            </p>
          </div>

          {/* Session Expired Banner */}
          {sessionExpiredBanner && (
            <div className="mb-5 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2.5">
              <Icon name="ClockIcon" size={16} className="text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-amber-700">Your session has expired. Please sign in again.</p>
            </div>
          )}

          {/* Alerts */}
          {error && (
            <div className="mb-5 p-3 bg-destructive/10 border border-destructive/20 rounded-lg flex items-start gap-2.5">
              <Icon name="ExclamationTriangleIcon" size={16} className="text-destructive flex-shrink-0 mt-0.5" />
              <p className="text-sm text-destructive">{error}</p>
            </div>
          )}
          {success && (
            <div className="mb-5 p-3 bg-green-50 border border-green-200 rounded-lg flex items-start gap-2.5">
              <Icon name="CheckCircleIcon" size={16} className="text-green-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-green-700">{success}</p>
            </div>
          )}

          {/* AGLC Model Notice for Register mode */}
          {mode === 'register' && (
            <div className="mb-5 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-start gap-2">
                <Icon name="InformationCircleIcon" size={14} className="text-blue-600 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-blue-700">
                  <strong>AGLC Delivery Model:</strong> You are applying as a <strong>licensed liquor retailer</strong>. You are responsible for all liquor sales and order fulfillment. Zalchos is a technology marketplace only.
                </p>
              </div>
            </div>
          )}

          {/* Sign In Form */}
          {mode === 'signin' ? (
            <form onSubmit={handleSignIn} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Email Address</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Icon name="EnvelopeIcon" size={18} className="text-muted-foreground" />
                  </div>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    placeholder="vendor@yourstore.com"
                    disabled={loading}
                    className="w-full pl-10 pr-4 py-2.5 bg-background border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors disabled:opacity-60"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Password</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Icon name="LockClosedIcon" size={18} className="text-muted-foreground" />
                  </div>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    placeholder="••••••••"
                    disabled={loading}
                    className="w-full pl-10 pr-10 py-2.5 bg-background border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors disabled:opacity-60"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  >
                    <Icon name={showPassword ? 'EyeSlashIcon' : 'EyeIcon'} size={18} className="text-muted-foreground hover:text-foreground" />
                  </button>
                </div>
              </div>
              <div className="flex items-center justify-end">
                <Link href="/forgot-password" className="text-xs text-primary hover:underline">Forgot password?</Link>
              </div>
              <button
                type="submit"
                disabled={loading}
                className="w-full py-2.5 bg-primary text-primary-foreground rounded-lg text-sm font-semibold hover:bg-primary/90 disabled:opacity-60 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
                    Signing In...
                  </>
                ) : 'Sign In to Vendor Portal'}
              </button>
            </form>
          ) : (
            /* Register Form */
            <form onSubmit={handleRegister} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Store / Business Name</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Icon name="BuildingStorefrontIcon" size={18} className="text-muted-foreground" />
                  </div>
                  <input
                    type="text"
                    value={storeName}
                    onChange={(e) => setStoreName(e.target.value)}
                    required
                    placeholder="Your Store Name"
                    disabled={loading}
                    className="w-full pl-10 pr-4 py-2.5 bg-background border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors disabled:opacity-60"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Email Address</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Icon name="EnvelopeIcon" size={18} className="text-muted-foreground" />
                  </div>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    placeholder="vendor@yourstore.com"
                    disabled={loading}
                    className="w-full pl-10 pr-4 py-2.5 bg-background border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors disabled:opacity-60"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Password</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Icon name="LockClosedIcon" size={18} className="text-muted-foreground" />
                  </div>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    placeholder="Min. 8 characters"
                    disabled={loading}
                    className="w-full pl-10 pr-10 py-2.5 bg-background border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors disabled:opacity-60"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  >
                    <Icon name={showPassword ? 'EyeSlashIcon' : 'EyeIcon'} size={18} className="text-muted-foreground hover:text-foreground" />
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Confirm Password</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Icon name="LockClosedIcon" size={18} className="text-muted-foreground" />
                  </div>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    placeholder="Re-enter password"
                    disabled={loading}
                    className="w-full pl-10 pr-4 py-2.5 bg-background border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors disabled:opacity-60"
                  />
                </div>
              </div>
              <button
                type="submit"
                disabled={loading}
                className="w-full py-2.5 bg-primary text-primary-foreground rounded-lg text-sm font-semibold hover:bg-primary/90 disabled:opacity-60 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
                    Submitting Application...
                  </>
                ) : 'Apply as Vendor'}
              </button>
            </form>
          )}

          {/* Footer */}
          <div className="mt-6 pt-6 border-t border-border text-center space-y-2">
            <p className="text-xs text-muted-foreground">
              Admin?{' '}
              <Link href="/apps/admin/login" className="text-primary hover:underline font-medium">
                Sign in to Admin Portal
              </Link>
            </p>
          </div>
        </div>

        {/* Compliance badges */}
        <div className="mt-6 flex items-center justify-center gap-4 text-xs text-muted-foreground">
          <span className="flex items-center gap-1">
            <Icon name="ShieldCheckIcon" size={14} className="text-primary" />
            SSL Secured
          </span>
          <span className="flex items-center gap-1">
            <Icon name="LockClosedIcon" size={14} className="text-primary" />
            AGLC Compliant
          </span>
          <span className="flex items-center gap-1">
            <Icon name="CheckBadgeIcon" size={14} className="text-primary" />
            Licensed Retailers Only
          </span>
        </div>
        <p className="text-center text-xs text-muted-foreground mt-2">
          Zalchos is a technology marketplace. All liquor sales are made by licensed retailers.
        </p>
      </div>
    </div>
  );
}
